<template>
  <div class="flex flex-col gap-4">
    <FaqCreateModal ref="faqCreateModal" :business-system-id="businessSystemId" @saved="handleFaqSaved" />

    <Card>
      <CardHeader class="flex flex-row items-center justify-between">
        <CardTitle>FAQs</CardTitle>
        <Button size="sm" :disabled="!isValidBusinessSystemId" @click="openFaqCreateModal">
          Create FAQ
        </Button>
      </CardHeader>
      <CardContent class="flex flex-col gap-3">
        <p v-if="faqStore.isLoading" class="text-sm text-muted-foreground">
          Loading FAQs...
        </p>
        <p v-else-if="faqs.length === 0" class="text-sm text-muted-foreground">
          No FAQs yet.
        </p>
        <UmisisAnimatedList v-else :items="faqs" list-class="flex flex-col gap-2">
          <template #default="{ item: faq }">
            <FaqItem
              :faq="faq"
              :is-deleting="isDeletingFaq(faq.id)"
              @edit="handleEditFaq"
              @delete="handleDeleteFaq"
            />
          </template>
        </UmisisAnimatedList>
      </CardContent>
    </Card>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { toast } from 'vue-sonner'
import UmisisAnimatedList from '../../../../../../../../resources/js/components/umisis/generic/UmisisAnimatedList.vue'
import FaqCreateModal from '../../../components/business-systems-faqs/FaqCreateModal.vue'
import FaqItem from '../../../components/business-systems-faqs/FaqItem.vue'
import { Button } from '../../../../../../../../resources/js/components/ui/button/index.js'
import { Card, CardContent, CardHeader, CardTitle } from '../../../../../../../../resources/js/components/ui/card/index.js'
import { useFaqStore } from '../../../stores/faqs.js'

const route = useRoute()
const faqStore = useFaqStore()
const faqCreateModal = ref(null)

const businessSystemId = computed(() => Number(route.params.businessSystem ?? 0))
const isValidBusinessSystemId = computed(() => Number.isInteger(businessSystemId.value) && businessSystemId.value > 0)
const faqs = computed(() => {
  return [...faqStore.items].sort((firstFaq, secondFaq) => {
    const firstSortOrder = Number(firstFaq?.sort_order ?? 0)
    const secondSortOrder = Number(secondFaq?.sort_order ?? 0)

    if (firstSortOrder === secondSortOrder) {
      return Number(secondFaq?.id ?? 0) - Number(firstFaq?.id ?? 0)
    }

    return firstSortOrder - secondSortOrder
  })
})

const deletingFaqIds = ref([])

const openFaqCreateModal = () => {
  faqCreateModal.value?.openCreateModal()
}

const isDeletingFaq = (faqId) => {
  return deletingFaqIds.value.includes(Number(faqId))
}

const loadFaqs = async () => {
  if (!isValidBusinessSystemId.value) {
    faqStore.items = []
    return
  }

  try {
    await faqStore.fetchFaqsForBusinessSystem(businessSystemId.value)
  } catch (error) {
    toast.error(error?.response?.data?.message ?? 'Unable to load FAQs.')
  }
}

const handleFaqSaved = async () => {
  await loadFaqs()
}

const handleEditFaq = (faq) => {
  faqCreateModal.value?.openEditModal(faq)
}

const handleDeleteFaq = async (faq) => {
  const normalizedFaqId = Number(faq?.id)
  deletingFaqIds.value = [...new Set([...deletingFaqIds.value, normalizedFaqId])]

  try {
    await faqStore.itemDelete(normalizedFaqId)
    toast.success('FAQ deleted.')
  } catch (error) {
    toast.error(error?.response?.data?.message ?? 'Unable to delete FAQ.')
  } finally {
    deletingFaqIds.value = deletingFaqIds.value.filter((faqId) => faqId !== normalizedFaqId)
  }
}

watch(businessSystemId, async () => {
  await loadFaqs()
})

onMounted(async () => {
  await loadFaqs()
})
</script>
